import styles from '../components/LatexHTMLRenderer/LatexHTMLRenderer.module.scss';

interface ListItemJson {
  type: string;
  subType?: string;
  contentList: Array<{
    type: string;
    schema?: string;
    content?: any[];
  }>;
}

interface PopupNode {
  type: string;
  content?: any[];
  rows?: TableRow[];
}

interface TableRow {
  cells: TableCell[];
}

interface TableCell {
  content?: any[];
}

function preprocessListContent(listJson: { type: string; listItems: ListItemJson[] }): string {
  if (!listJson || listJson.type !== 'list' || !Array.isArray(listJson.listItems)) {
    return '';
  }

  const renderTextContent = (contentArr: any[]): string => {
    return contentArr
      .map((c) => {
        if (c.type === 'text') {
          return c.text || '';
        }
        if (c.type === 'contentLink') {
          const linkText = renderTextContent(c.link?.content || []);
          if (c.popup?.externalUrl) {
            // External link
            return `<a href="${c.popup.externalUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
          } else if (c.popup?.content) {
            // Inline popup
            const popupId = `popup-${c.id || Math.random().toString(36).substring(2)}`;
            const popupHTML = renderPopupContent(c.popup.content);
            return `<a href="#" class="inline-popup" data-popup-id="${popupId}">${linkText}</a>
                    <div id="${popupId}" class="popup-hidden" style="display:none;">${popupHTML}</div>`;
          }
        }
        return '';
      })
      .join('');
  };

  const renderPopupContent = (popupArr: PopupNode[]): string => {
    const renderTable = (rows: TableRow[]): string => {
      const renderRow = (row: TableRow): string => {
        const cellsHTML = row.cells
          .map((cell) => `<td>${renderTextContent(cell.content?.[0]?.content || [])}</td>`)
          .join('');
        return `<tr>${cellsHTML}</tr>`;
      };
      return `<table border="1">${rows.map(renderRow).join('')}</table>`;
    };

    return popupArr
      .map((node) => {
        switch (node.type) {
          case 'paragraph':
            return `<p>${renderTextContent(node.content || [])}</p>`;
          case 'table':
            return node.rows ? renderTable(node.rows) : '';
          default:
            return '';
        }
      })
      .join('');
  };

  const listItemsHTML = listJson.listItems
    .map((li: ListItemJson) => {
      const liContent = li.contentList
        .map((block) => {
          if (block.type === 'paragraph') {
            return `<p>${renderTextContent(block.content || [])}</p>`;
          }
          return '';
        })
        .join('');
      return `<li>${liContent}</li>`;
    })
    .join('');

  // Wrap with container for CSS modules
  return `<div class=${styles.listContainer}><ol type="1">${listItemsHTML}</ol></div>`;
}

/**
 * Preprocesses embedded JSON "list" data inside content strings.
 * Expects format: %%LIST%% { ...json... } %%LIST%%
 */
export function preprocessListJson(content: string): string {
  // The [\s\S]*? pattern matches absolutely anything, including newlines
  const listRegex = /%%LIST%%([\s\S]*?)%%LIST%%/g;

  return content.replace(listRegex, (match, jsonData) => {
    try {
      const listData = JSON.parse(jsonData.trim());

      // Validate structure
      if (
        !listData ||
        listData.type !== 'list' ||
        !Array.isArray(listData.listItems) ||
        listData.listItems.length === 0
      ) {
        console.error('Invalid list data structure', listData);
        return '<div class="list-error">Invalid list data</div>';
      }

      // Convert JSON → HTML
      return preprocessListContent(listData);
    } catch (error) {
      console.error('Error parsing list data:', error);
      return '<div class="list-error">Error rendering list</div>';
    }
  });
}
